import React from 'react';
import styled, { keyframes } from 'styled-components';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDocTitle } from '../hooks/useDocTitle';
import logoWhite from '../assets/logo_white_transparent.png';

const glitch = keyframes`
  0%,100%{clip-path:inset(0 0 98% 0);transform:translateX(0)}
  10%{clip-path:inset(30% 0 50% 0);transform:translateX(-4px)}
  20%{clip-path:inset(60% 0 10% 0);transform:translateX(4px)}
  30%{clip-path:inset(10% 0 70% 0);transform:none}
`;
const fadeUp = keyframes`from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:none}`;
const gridAnim = keyframes`from{background-position:0 0}to{background-position:0 60px}`;

const Page = styled.div`
  min-height: 100vh; overflow-x: hidden; background: #02020E;
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  position: relative; overflow: hidden; padding: 40px 24px;
`;
const GridBg = styled.div`
  position: fixed; inset: 0; pointer-events: none;
  background-image:
    linear-gradient(rgba(0,163,255,.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(0,163,255,.03) 1px, transparent 1px);
  background-size: 60px 60px; animation: ${gridAnim} 20s linear infinite;
`;
const Content = styled.div`position:relative;z-index:2;text-align:center;animation:${fadeUp} .6s ease both;`;
const LogoWrap = styled.div`margin-bottom:48px; img{height:52px;object-fit:contain;margin:0 auto;display:block;}`;
const Code = styled.div`
  font-family:'Bebas Neue',sans-serif;font-size:clamp(100px,22vw,200px);line-height:1;
  background:linear-gradient(135deg,#00A3FF,#C800FF);-webkit-background-clip:text;-webkit-text-fill-color:transparent;
  position:relative;letter-spacing:.04em;
  &::before{content:'404';position:absolute;inset:0;background:linear-gradient(135deg,#C800FF,#00A3FF);-webkit-background-clip:text;-webkit-text-fill-color:transparent;animation:${glitch} 3s ease infinite;}
`;
const Title = styled.h1`font-family:'Orbitron',monospace;font-size:clamp(14px,2.5vw,20px);font-weight:700;letter-spacing:.2em;color:rgba(255,255,255,.7);text-transform:uppercase;margin:8px 0 20px;`;
const Desc = styled.p`font-family:'Rajdhani',sans-serif;font-size:16px;letter-spacing:.08em;color:rgba(255,255,255,.35);max-width:380px;line-height:1.7;margin-bottom:40px;`;
const BtnRow = styled.div`display:flex;gap:12px;justify-content:center;flex-wrap:wrap;`;
const BtnPrimary = styled(Link)`
  font-family:'Rajdhani',sans-serif;font-weight:700;font-size:13px;letter-spacing:.2em;
  text-transform:uppercase;padding:13px 32px;text-decoration:none;
  background:linear-gradient(135deg,#00A3FF,#6B00FF);color:#fff;
  clip-path:polygon(10px 0%,100% 0%,calc(100% - 10px) 100%,0% 100%);transition:all .3s;
  &:hover{box-shadow:0 0 24px rgba(0,163,255,.5);transform:translateY(-2px);}
`;
const BtnOutline = styled(Link)`
  font-family:'Rajdhani',sans-serif;font-weight:700;font-size:13px;letter-spacing:.2em;
  text-transform:uppercase;padding:13px 32px;text-decoration:none;
  border:1px solid rgba(255,255,255,.15);color:rgba(255,255,255,.5);
  clip-path:polygon(10px 0%,100% 0%,calc(100% - 10px) 100%,0% 100%);transition:all .3s;
  &:hover{border-color:rgba(0,163,255,.4);color:#00A3FF;}
`;

export default function NotFound() {
  const { t } = useTranslation();
  useDocTitle('pageTitles.notFound');

  return (
    <Page>
      <GridBg />
      <Content>
        <LogoWrap><img src={logoWhite} alt="Guebly Games" /></LogoWrap>
        <Code>404</Code>
        <Title>{t('notFound.title')}</Title>
        <Desc>{t('notFound.desc')}</Desc>
        <BtnRow>
          <BtnPrimary to="/">{t('notFound.btnBack')}</BtnPrimary>
          <BtnOutline to="/games">{t('notFound.btnGames')}</BtnOutline>
        </BtnRow>
      </Content>
    </Page>
  );
}
